import { oc } from "@orpc/contract";
import { array, number, object, string, unknown, url } from "zod";
import { Account, AccountDailyBalancesReport, User } from "./db/models.ts";
import {
  AccessGrant,
  AccessRequest,
  LoginLinkRequest,
  LoginLinkSendConfirmation,
} from "./iam/schemas.ts";
import { HTTPServerConfiguration, ServiceHealthCheckReport } from "./schema.ts";
import { ImportArgs, ImportSuccess } from "./schema/import-meals.ts";
import { RecipeURLCrawlReport } from "./schema/recipe-url-crawl-report.ts";
import {
  DownloadedYouTubeMedia,
  YouTubeMediaDownloadFormat,
  YouTubeVideoURLInput,
} from "./schema/youtube-media.js";
import {
  TaskDocument,
  TaskDocumentEntry,
  TaskDocumentIdentifier,
} from "./services/task-documents.ts";

function markdown(strings: TemplateStringsArray, ...values: unknown[]): string {
  return (
    String.raw({ raw: strings }, ...values)
      // .replace(/\n+/g, "\n")
      .trim()
  );
}

const ping = oc
  .route({ method: "GET", path: `/api/ping` })
  .input(
    object({
      message: string().meta({
        description: markdown`
What message do you want to send? (e.g.: \`"some message"\`)

## Example

~~~ts
const message = "some message";
~~~
        `,
      }),
    }),
  )
  .output(object({ message: string() }));

const recipe = oc
  .route({ method: "GET", path: `/api/recipe` })
  .input(object({ recipeURL: url() }))
  .output(RecipeURLCrawlReport);

const youtubeDownloadMedia = oc
  .route({
    description: "Download a YouTube video and return its local file details.",
    method: "POST",
    path: "/api/youtube/download-media",
  })
  .input(
    object({
      format: YouTubeMediaDownloadFormat.optional().default(
        YouTubeMediaDownloadFormat.enum.AudioVideo,
      ),
      videoURL: YouTubeVideoURLInput,
    }),
  )
  .output(DownloadedYouTubeMedia);

const jsonSchema = oc
  .route({ method: "GET", path: "/api/json-schema" })
  .input(
    object({
      slug: string().trim().nonempty().meta({
        description: "This slug is used to uniquely identify a resource type.",
      }),
    }),
  )
  .output(object({}).passthrough());

const server = oc
  .route({ method: "GET", path: "/api/server" })
  .input(object({}))
  .output(HTTPServerConfiguration);

const importMeals = oc
  .route({ method: "GET", path: "/api/import-meals" })
  .input(ImportArgs)
  .output(ImportSuccess);

const health = oc
  .route({ method: "GET", path: "/api/health" })
  .input(object({}))
  .output(array(ServiceHealthCheckReport));

const taskDocumentList = oc
  .route({ method: "GET", path: "/api/tasks" })
  .input(object({}))
  .output(array(TaskDocumentEntry));

const taskDocumentRead = oc
  .route({ method: "GET", path: "/api/tasks/read" })
  .input(object({ id: TaskDocumentIdentifier }))
  .output(TaskDocument);

const accountingAccountBalances = oc
  .route({ method: "POST", path: "/api/accounting/account-balances" })
  .input(
    object({
      accountIds: array(Account.shape.id),
      fromDate: string().datetime().nullish(),
      toDate: string().datetime().nullish(),
    }),
  )
  .output(AccountDailyBalancesReport);

const accountingListAccounts = oc
  .route({ method: "GET", path: "/api/accounting/list-accounts" })
  .input(object({}))
  .output(array(Account));

const accountingCreateAccount = oc
  .route({ method: "POST", path: "/api/accounting/create-account" })
  .input(
    object({
      name: string().trim().nonempty(),
    }),
  )
  .output(Account);

const accountingImportAccounts = oc
  .route({ method: "POST", path: "/api/accounting/import-accounts" })
  .input(
    object({
      accountSheetName: string().trim().nonempty().optional().default("accounts"),
      sourceSpreadsheetId: string().trim().nonempty(),
    }),
  )
  .output(
    object({
      accountRows: array(array(unknown())),
      accountSheetName: string().trim().nonempty(),
      rowParsingIssues: array(
        object({
          issue: string().or(array(string())),
          rowNumber: number().int(),
        }),
      ).optional(),
    }),
  );

const iamRequestAccess = oc
  .route({ method: "POST", path: "/api/iam/request-access" })
  .input(AccessRequest)
  .output(AccessGrant);

const iamRequestLoginLink = oc
  .route({ method: "POST", path: "/api/iam/request-login-link" })
  .input(LoginLinkRequest)
  .output(LoginLinkSendConfirmation);

const iamUsers = oc
  .route({ method: "GET", path: "/api/iam/users" })
  .input(object({}))
  .output(array(User));

const iamWhoAmI = oc
  .route({ method: "GET", path: "/api/iam/who-am-i" })
  .input(object({}))
  .output(User);

const contract = {
  ping,
  recipe,
  jsonSchema,
  server,
  importMeals,
  health,
  tasks: {
    list: taskDocumentList,
    read: taskDocumentRead,
  },
  accounting: {
    accountBalances: accountingAccountBalances,
    createAccount: accountingCreateAccount,
    importAccounts: accountingImportAccounts,
    listAccounts: accountingListAccounts,
  },
  iam: {
    requestAccess: iamRequestAccess,
    requestLoginLink: iamRequestLoginLink,
    users: iamUsers,
    whoAmI: iamWhoAmI,
  },
  youtube: {
    downloadMedia: youtubeDownloadMedia,
  },
  mealtime: {
    crawlRecipeURL: oc
      .route({
        description: markdown`
          Read the given HTTP(S) URL and attempt to parse structured data from it (JSON-LD, OpenGraph, etc.)
        `,
        method: "GET",
        path: "/api/mealtime/crawl-recipe-url",
      })
      .input(
        object({
          recipeURL: url().meta({
            description: "What recipe URL should be crawled?",
          }),
        }),
      )
      .output(RecipeURLCrawlReport),
  },
};

export default contract;
